import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BreadCrumsHeader from "../../common/BreadCrums";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Status from "../../common/Status";
import Delete from "../../common/Delete";
import Edit from "../../common/Edit";
import Pagination from "../../common/TablePagination";
import CreateNewButton from "../../common/CreateNewButton";
import { ReactComponent as File } from "../../../Assets/Icons/fileIcon.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteAsset,
  getAssets,
  toggleAssetStatus,
} from "../../../services/assetsService";
import Loader from "../../common/Loader";
import deleteImg from "../../../Assets/Images/deleteImg.svg";
import CancelPopup from "../../common/CancelPopup";
import FundraiserPopup from "./FundraiserPopup";
import { ReactComponent as FundraiserIcon } from "../../../Assets/Icons/FundraiserIcon.svg";
import FilesPopup from "./FilesPopup";
import TableEmptyState from "../../common/TableEmptyState";

const DESCRIPTION_MAX_LENGTH = 50;
const TITLE_MAX_LENGTH = 30; // You can adjust this number as needed

const PromotionsListTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.assetsReducer?.loading);
  const location = useLocation();
  const currentPath = location.pathname;
  const [allAssets, setAllAssets] = useState([]);
  const [deleteClicked, setDeleteClicked] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [fundraisers, setFundraisers] = useState(null);
  const [isFundraisersOpen, setIsFundraisersOpen] = useState(false);
  const [isFilesOpen, setIsFilesOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedPromotionTitle, setSelectedPromotionTitle] = useState("");

  const getAssetsData = async () => {
    const assetsPromise = await dispatch(
      getAssets({ page: currentPage, limit: 10 })
    )?.unwrap();
    if (assetsPromise?.success === true) {
      setAllAssets(assetsPromise?.data);
      setCurrentPage(assetsPromise?.currentPage);
      setTotalPages(assetsPromise?.totalPages);
      setCount(assetsPromise?.count);
    }
  };

  useEffect(() => {
    getAssetsData();
  }, [deleteSuccess, currentPage]);

  const items = [
    {
      title: "Assets and  Promotions",
    },
    {
      title: "List of Promotions",
    },
  ];

  const columns = [
    {
      name: "No.",
    },
    {
      name: "Title",
    },
    {
      name: "Description",
    },
    {
      name: "File"
    },
    {
      name: "Fundraisers",
    },
    {
      name: "Status",
    },
    {
      name: "Actions",
    },
  ];

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleToggle = async (id) => {
    try {
      const res = await dispatch(toggleAssetStatus({ id: id })).unwrap();
      if (res?.success) {
        getAssetsData();
      }
    } catch (error) {
      console.error("Error toggling location status:", error);
    }
  };

  const handleEditClick = (id) => {
    navigate("/add-assets", {
      state: {
        id: id,
      },
    });
  };

  const handleDeleteClick = (id) => {
    setDeleteClicked(true);
    setSelectedAssetId(id);
  };

  const handleKeepAsset = () => {
    setDeleteClicked(false);
  };

  const handleDeleteAsset = async () => {
    if (selectedAssetId !== null) {
      const deletePromise = await dispatch(
        deleteAsset({ id: selectedAssetId })
      )?.unwrap();
      if (deletePromise?.success === true) {
        setDeleteClicked(false);
        setDeleteSuccess(!deleteSuccess);
      }
    }
  };

  const handleFundraiserClick = (fundraiser, promotionId) => {
    setFundraisers(fundraiser);
    setSelectedAssetId(promotionId);
    setIsFundraisersOpen(true);
  };

  const handleCloseFundraiser = () => {
    setIsFundraisersOpen(false);
  };

  const handleFileClick = (promotion) => {
    // If we have grouped files, show them all. If single, show single.
    const files = promotion.allFiles || (promotion.file ? [promotion.file] : []);
    // Also check for category-specific fields if file is missing
    if (files.length === 0) {
      const fieldPossibleNames = [
        'postcardStandard', 'a1Poster', 'a2Poster', 'a3Poster', 'a4Poster',
        'socialMediaBanner', 'digitalFlyer', 'qrCode', 'emailTemplate',
        'pullUpBanner', 'emailSignature'
      ];
      fieldPossibleNames.forEach(field => {
        if (promotion[field]) files.push(promotion[field]);
      });
    }

    setSelectedFiles(files);
    setSelectedPromotionTitle(promotion.title);
    setIsFilesOpen(true);
  };

  const handleCloseFiles = () => {
    setIsFilesOpen(false);
  };

  const handleFundraiserRemoved = () => {
    getAssetsData();
  };

  return (
    <>
      {loading === true && <Loader />}
      {fundraisers !== null && isFundraisersOpen === true && (
        <FundraiserPopup
          fundraisers={fundraisers}
          handleCloseFundraiser={handleCloseFundraiser}
          promotionId={selectedAssetId}
          onFundraiserRemoved={handleFundraiserRemoved}
        />
      )}
      {isFilesOpen && (
        <FilesPopup
          files={selectedFiles}
          title={selectedPromotionTitle}
          handleClose={handleCloseFiles}
        />
      )}
      <div className="flex justify-between col-span-12">
        <BreadCrumsHeader heading="List of Promotions" items={items} />
        <CreateNewButton
          text={"Create New Promotion"}
          navigateTo={"/add-assets"}
          iconClassName="sm:left-[1%] md:left-[4%] bottom-[30%]"
        />
      </div>
      {deleteClicked == true ? (
        <CancelPopup
          handleNo={handleKeepAsset}
          handleYes={handleDeleteAsset}
          heading={"Delete Promotion"}
          content={"Are you sure you want to delete this promotion?"}
          image={deleteImg}
        />
      ) : null}
      <div className=" col-span-12 mt-8">
        <>
          <TableContainer
            style={{
              width: "100%",
              overflowX: "auto"
            }}
          >
            <Table
              sx={{
                minWidth: "800px",
                borderCollapse: "separate",
                borderSpacing: "0px 8px",
              }}
              aria-label="simple table"
            >
              <TableHead className="bg-[#00ADEF]">
                <TableRow
                  className="h-[50px]"
                  sx={{ boxShadow: "0px 4px 4px 0px #00000040" }}
                >
                  {columns.map((column, index) => {
                    return (
                      <TableCell
                        key={index}
                        sx={{
                          color: '#FFFFFF',
                          fontWeight: 500,
                          fontSize: '14px',
                          textAlign: column.name === 'File' || column.name === 'Fundraisers' || column.name === 'Status' || column.name === 'Actions' ? 'center' : 'left'
                        }}
                      >
                        {column?.name}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>

              <TableBody>
                {(() => {
                  // Grouping logic: We group by title and description to show multiple assets together
                  const groupedAssets = [];
                  allAssets?.forEach(asset => {
                    const existing = groupedAssets.find(g =>
                      g.title?.trim() === asset.title?.trim() &&
                      g.description?.trim() === asset.description?.trim()
                    );

                    const categories = [
                      { field: 'postcardStandard', label: 'Postcard' },
                      { field: 'a1Poster', label: 'A1 Poster' },
                      { field: 'a2Poster', label: 'A2 Poster' },
                      { field: 'a3Poster', label: 'A3 Poster' },
                      { field: 'a4Poster', label: 'A4 Poster' },
                      { field: 'socialMediaBanner', label: 'Social Media Banner' },
                      { field: 'digitalFlyer', label: 'Digital Flyer' },
                      { field: 'qrCode', label: 'QR Code' },
                      { field: 'emailTemplate', label: 'Email Template' },
                      { field: 'pullUpBanner', label: 'Pull-up Banner' },
                      { field: 'emailSignature', label: 'Email Signature' }
                    ];

                    const getFilesFromAsset = (a) => {
                      const files = [];
                      if (a.file) files.push({ url: a.file, category: a.category || 'Asset' });
                      categories.forEach(cat => {
                        if (a[cat.field]) files.push({ url: a[cat.field], category: cat.label });
                      });
                      return files;
                    };

                    const currentFiles = getFilesFromAsset(asset);

                    if (existing) {
                      currentFiles.forEach(cf => {
                        if (!existing.allFiles.some(f => f.url === cf.url)) {
                          existing.allFiles.push(cf);
                        }
                      });
                    } else {
                      const newGroup = { ...asset, allFiles: currentFiles };
                      groupedAssets.push(newGroup);
                    }
                  });

                  return groupedAssets.map((promotion, index) => {
                    return (
                      <TableRow
                        key={promotion._id || index}
                        className="h-[50px]"
                        sx={{
                          boxShadow: "0px 4px 4px 0px #00000040",
                          backgroundColor:
                            (index + 1) % 2 !== 0 ? "#F5F5F5" : "#FFFFFF",
                        }}
                      >
                        <TableCell sx={{ color: '#000000', fontSize: '14px' }}>{(currentPage - 1) * 10 + index + 1}</TableCell>
                        <TableCell sx={{ color: '#000000', fontSize: '14px' }}>
                          <div title={promotion?.title} className="max-w-[200px] truncate font-medium">
                            {promotion?.title}
                          </div>
                        </TableCell>
                        <TableCell sx={{ color: '#000000', fontSize: '14px' }}>
                          <div title={promotion?.description} className="max-w-[300px] truncate text-[#767676]">
                            {promotion?.description}
                          </div>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <div className="max-w-[80px] flex justify-center mx-auto">
                            {(promotion?.file || promotion?.allFiles?.length > 0) ? (
                              <div
                                className="relative cursor-pointer hover:scale-110 transition-transform"
                                onClick={() => handleFileClick(promotion)}
                              >
                                <File className="w-[30px] h-[30px]" />
                                {promotion?.allFiles?.length > 1 && (
                                  <span className="absolute -top-1 -right-1 bg-[#00ADEF] text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center border border-white">
                                    {promotion.allFiles.length}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <div className="max-w-[60px] flex justify-center mx-auto">
                            <FundraiserIcon
                              className="cursor-pointer hover:scale-110 transition-transform duration-200 ease-in-out active:scale-95"
                              style={{
                                imageRendering: '-webkit-optimize-contrast',
                                transform: 'translateZ(0)',
                                backfaceVisibility: 'hidden'
                              }}
                              onClick={() =>
                                handleFundraiserClick(promotion?.fundraisers, promotion?._id)
                              }
                            />
                          </div>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Status
                            isEnabled={promotion?.status}
                            handleToggle={() => handleToggle(promotion?._id)}
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <div className="flex justify-center gap-[4%]">
                            <Edit onClick={() => handleEditClick(promotion?._id)} tooltip={"edit asset"} />
                            <Delete
                              onClick={() => handleDeleteClick(promotion?._id)}
                              tooltip={"delete asset"}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  });
                })()}

                {!loading && allAssets && allAssets.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      className="!border-b-0 !p-0"
                    >
                      <TableEmptyState itemName="Promotion" />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {allAssets && allAssets.length > 0 && (
            <Pagination
              totalPages={totalPages}
              handlePageChange={handlePageChange}
            />
          )}
        </>

      </div >
    </>
  );
};

export default PromotionsListTable;
